export const MAX_TICKETS_PER_PRODUCT = 20;

export const productsEnabledForSquad = (products) =>
  (products || []).filter((product) => product && product.isDeleted !== true);

export const isProductShuffledOnSquad = (product, squadId) =>
  Boolean(
    (product?.bracketProductSquad || []).find((row) => row.squadID === squadId)
      ?.shuffledAt
  );

export const clampTicketCount = (value) => {
  const next = Math.floor(Number(value) || 0);
  if (next < 0) return 0;
  if (next > MAX_TICKETS_PER_PRODUCT) return MAX_TICKETS_PER_PRODUCT;
  return next;
};

export const normalizeTicketCounts = (counts) => {
  const next = {};
  Object.entries(counts || {}).forEach(([productId, value]) => {
    const count = clampTicketCount(value);
    if (count > 0) next[productId] = count;
  });
  return next;
};

export const hasTicketCounts = (counts) =>
  Object.values(counts || {}).some((value) => clampTicketCount(value) > 0);

export const countsByBowlerPayload = (countsByBowler) => {
  const next = {};
  Object.entries(countsByBowler || {}).forEach(([name, counts]) => {
    const normalized = normalizeTicketCounts(counts);
    if (Object.keys(normalized).length) next[name] = normalized;
  });
  return next;
};

export const withBowlerTicketCount = (
  tickets,
  { productId, squadId, bowlerId, nextCount }
) => {
  const list = tickets || [];
  const mine = list.filter((ticket) => ticket.bowlerID === bowlerId);
  const others = list.filter((ticket) => ticket.bowlerID !== bowlerId);
  const target = clampTicketCount(nextCount);
  if (target === mine.length) return list;
  if (target > mine.length) {
    const extras = Array.from({ length: target - mine.length }, (_, index) => ({
      id: `temp-${bowlerId}-${mine.length + index}`,
      productID: productId,
      squadID: squadId,
      bowlerID: bowlerId,
      treeID: null,
      seedPosition: null,
      isLeftover: false,
    }));
    return [...others, ...mine, ...extras];
  }
  return [...others, ...mine.slice(0, target)];
};

export const countsFromTickets = (tickets, products, squadId, bowlerId) => {
  const counts = {};
  (products || []).forEach((product) => {
    counts[product.id] = (tickets || []).filter(
      (ticket) =>
        ticket.productID === product.id &&
        ticket.squadID === squadId &&
        ticket.bowlerID === bowlerId
    ).length;
  });
  return counts;
};

export const ticketCost = (products, counts) =>
  (products || []).reduce((sum, product) => {
    const count = clampTicketCount(counts?.[product.id]);
    return sum + count * (Number(product.entryAmount) || 0);
  }, 0);

export const assignmentBowlerId = (row) =>
  row?.bowlerID || row?.bowlers?.id || null;

export const matchCreatedBowlers = (assignments, formBowlers) => {
  const remaining = [...(assignments || [])];
  return (formBowlers || []).map((formBowler) => {
    const existingId =
      formBowler?.existingBowler &&
      formBowler.id &&
      !String(formBowler.id).startsWith("temp-")
        ? formBowler.id
        : null;
    if (existingId) {
      const index = remaining.findIndex(
        (row) => assignmentBowlerId(row) === existingId
      );
      if (index >= 0) {
        const [row] = remaining.splice(index, 1);
        return { formBowler, bowlerId: assignmentBowlerId(row) };
      }
    }
    const name = String(formBowler?.name || "").trim().toLowerCase();
    const index = remaining.findIndex(
      (row) =>
        String(row?.bowlers?.name || row?.name || "")
          .trim()
          .toLowerCase() === name
    );
    if (index >= 0) {
      const [row] = remaining.splice(index, 1);
      return { formBowler, bowlerId: assignmentBowlerId(row) };
    }
    return { formBowler, bowlerId: null };
  });
};

export const createdTeamFromApprove = (payload) => {
  if (!payload) return null;
  if (Array.isArray(payload.team?.bowlers)) return payload.team;
  if (Array.isArray(payload.team?.team?.bowlers)) return payload.team.team;
  if (Array.isArray(payload.bowlers) && payload.id) return payload;
  return null;
};
